import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';
import type { VisualizationKind } from '@quartup/reports-shared';
import type { WidgetExecuteRequest, WidgetResult } from '../lib/dashboards-client.js';
import { WidgetError } from './widget-error.js';
import { WidgetRenderer } from './widget-renderer.js';
import { WidgetSkeleton } from './widget-skeleton.js';

interface WidgetTileProps {
  widgetId: string;
  title: string;
  visualization: VisualizationKind;
  vizConfig?: Record<string, unknown> | undefined;
  result: WidgetResult | undefined;
  isLoading: boolean;
  /** Called when the user clicks the per-widget refresh button */
  onRefresh: (req: WidgetExecuteRequest) => Promise<void> | void;
  globalParams: Record<string, unknown>;
  widgetParams?: Record<string, unknown> | undefined;
}

export function WidgetTile({
  widgetId,
  title,
  visualization,
  vizConfig,
  result,
  isLoading,
  onRefresh,
  globalParams,
  widgetParams,
}: WidgetTileProps) {
  function handleRefresh() {
    void onRefresh({
      global_params: globalParams,
      ...(widgetParams !== undefined ? { widget_params: widgetParams } : {}),
    });
  }

  return (
    <article
      className={cn(
        'flex flex-col rounded-[var(--radius-card)] border border-[var(--color-qup-border-dark)]',
        'bg-[var(--color-qup-bg-card-dark)] shadow-[var(--shadow-card)]',
        'transition-transform duration-300 hover:-translate-y-[3px] hover:shadow-[var(--shadow-card-hover)]',
      )}
      data-testid={`widget-tile-${widgetId}`}
    >
      {/* Tile header */}
      <div className="flex items-center justify-between border-b border-[var(--color-qup-border-dark)] px-4 py-3">
        <h3 className="text-sm font-semibold text-[var(--color-qup-text-dark)]">{title}</h3>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={isLoading}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-[var(--radius-button)] px-2 py-1',
            'text-xs font-medium text-[var(--color-qup-text-muted-dark)]',
            'hover:bg-[var(--color-qup-bg-dark)] hover:text-[var(--color-qup-text-dark)]',
            'transition-colors duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]',
            'disabled:pointer-events-none disabled:opacity-40',
          )}
          aria-label={t('dashboards.refresh_widget')}
          title={t('dashboards.refresh_widget')}
        >
          <svg
            className={cn('h-3.5 w-3.5', isLoading && 'animate-spin')}
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span className="sr-only">{t('dashboards.refresh_widget')}</span>
        </button>
      </div>

      {/* Tile body */}
      <div className="relative flex-1 p-4">
        {isLoading && result === undefined ? (
          <WidgetSkeleton />
        ) : result === undefined ? (
          /* Not yet loaded — show minimal placeholder */
          <div className="flex h-24 items-center justify-center">
            <p className="text-xs text-[var(--color-qup-text-muted-dark)]">No data</p>
          </div>
        ) : result.status === 'error' ? (
          <WidgetError
            {...(result.error.code !== undefined ? { errorCode: result.error.code } : {})}
            {...(result.error.message !== undefined ? { errorMessage: result.error.message } : {})}
            {...(result.error.requestId !== undefined ? { requestId: result.error.requestId } : {})}
          />
        ) : (
          /* D-3: when refreshing a widget that already has data, render an overlay instead of
             replacing the content — keeps the current result visible while the new one loads. */
          <div className={cn('relative', isLoading && 'opacity-50 pointer-events-none')}>
            <WidgetRenderer
              type={visualization}
              rows={result.rows}
              {...(vizConfig !== undefined ? { vizConfig } : {})}
            />
            {isLoading && (
              <div
                className="absolute right-2 top-2 flex items-center justify-center"
                aria-label="Refreshing widget"
                data-testid="widget-refresh-overlay"
              >
                <svg
                  className="h-4 w-4 animate-spin text-[var(--color-qup-primary)]"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
